import type { AudioSession, Unsubscribe } from '../client/types';

export interface PlayerOptions {
  /** Prefer WebCodecs AudioDecoder when the browser has it (flag; WASM is the default path). */
  webCodecs?: boolean;
  /** Initial jitter buffer target; adapts between 80 and 300 ms. */
  bufferMs?: number;
}

export interface PlayerStats {
  bufferMs: number;
  decoder: 'wasm' | 'webcodecs' | 'none';
  /** worklet = AudioWorklet ring buffer (secure contexts); scheduled = AudioBufferSource queue (plain http). */
  path: 'worklet' | 'scheduled' | 'none';
  underruns: number;
  framesDecoded: number;
  level: number;
}

/**
 * Browser playback for a listen session.
 *
 *   Opus 20 ms frames -> decoder (opus-decoder WASM via dynamic import, or WebCodecs AudioDecoder
 *   behind a flag) -> AudioWorklet ring buffer with an adaptive 80-300 ms jitter target.
 *
 * iOS Safari requires `resume()` from a user gesture; callers start this from the Listen button handler.
 * The worklet code is inlined as a Blob so it ships inside the app bundle without extra assets.
 */
export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  // Scheduled path (no AudioWorklet: the local UI is served over plain http on the LAN/hotspot,
  // which is not a secure context, so `ctx.audioWorklet` is undefined there): decoded 20 ms
  // frames are queued as AudioBufferSourceNodes behind a jitter target.
  private out: GainNode | null = null;
  private nextTime = 0;
  private peak = 0;
  private lastStatsAt = 0;
  private decoder: {
    decodeFrame(d: Uint8Array): Promise<{ channelData: Float32Array[]; samplesDecoded: number }>;
    free(): void;
  } | null = null;
  private wc: AudioDecoder | null = null;
  private unsubs: Unsubscribe[] = [];
  stats: PlayerStats = {
    bufferMs: 120,
    decoder: 'none',
    path: 'none',
    underruns: 0,
    framesDecoded: 0,
    level: -60,
  };
  private onStats?: (s: PlayerStats) => void;

  constructor(private readonly o: PlayerOptions = {}) {
    this.stats.bufferMs = o.bufferMs ?? 120;
  }

  async attach(session: AudioSession, onStats?: (s: PlayerStats) => void): Promise<void> {
    this.onStats = onStats;
    if (typeof AudioContext === 'undefined') return; // SSR / tests
    this.ctx = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' });
    await this.ctx.resume();
    let worklet = false;
    if (this.ctx.audioWorklet) {
      // The module ships as a blob: URL. A Content-Security-Policy without `blob:` in script-src
      // refuses it ("Unable to load a worklet's module"), which on the cloud console used to end the
      // whole listen session a moment after the button lit up (bench 2026-10-19). Fall back to the
      // scheduled path instead: that is what the local shell (plain http, no worklet) always uses.
      const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
      try {
        await this.ctx.audioWorklet.addModule(url);
        worklet = true;
      } catch (e) {
        console.warn('[listen] audio worklet unavailable, using scheduled playback', e);
      } finally {
        URL.revokeObjectURL(url);
      }
    }
    if (worklet) {
      this.node = new AudioWorkletNode(this.ctx, 'glyph-jitter', { outputChannelCount: [1] });
      this.node.port.onmessage = (m) => {
        if (m.data?.type === 'stats') {
          this.stats = {
            ...this.stats,
            bufferMs: m.data.bufferMs,
            underruns: m.data.underruns,
            level: m.data.level,
          };
          this.onStats?.(this.stats);
        }
      };
      this.node.port.postMessage({ type: 'target', ms: this.stats.bufferMs });
      this.node.connect(this.ctx.destination);
      this.stats.path = 'worklet';
    } else {
      this.out = this.ctx.createGain();
      this.out.connect(this.ctx.destination);
      this.nextTime = 0;
      this.stats.path = 'scheduled';
    }
    await this.initDecoder();
    this.unsubs.push(session.onFrame((f) => void this.push(f.opus)));
  }

  private async initDecoder() {
    if (this.o.webCodecs && typeof AudioDecoder !== 'undefined') {
      try {
        const sup = await AudioDecoder.isConfigSupported({
          codec: 'opus',
          sampleRate: 48000,
          numberOfChannels: 1,
        });
        if (sup.supported) {
          this.wc = new AudioDecoder({
            output: (data) => {
              const buf = new Float32Array(data.numberOfFrames);
              data.copyTo(buf, { planeIndex: 0, format: 'f32-planar' });
              data.close();
              this.emitPcm(buf);
            },
            error: (e) => console.warn('[listen] webcodecs error', e),
          });
          this.wc.configure({ codec: 'opus', sampleRate: 48000, numberOfChannels: 1 });
          this.stats.decoder = 'webcodecs';
          return;
        }
      } catch {
        /* fall through to WASM */
      }
    }
    try {
      // Dynamic import keeps the ~200 KB WASM decoder out of the main bundle until Listen is pressed.
      const mod = await import('opus-decoder');
      const dec = new mod.OpusDecoder({ channels: 1, sampleRate: 48000 });
      await dec.ready;
      this.decoder = dec as unknown as typeof this.decoder;
      this.stats.decoder = 'wasm';
    } catch (e) {
      console.warn('[listen] opus-decoder unavailable', e);
      this.stats.decoder = 'none';
    }
  }

  /** One decoded mono 48 kHz frame to the output path. */
  private emitPcm(pcm: Float32Array) {
    this.stats.framesDecoded++;
    if (this.node) {
      this.node.port.postMessage({ type: 'pcm', pcm }, [pcm.buffer]);
      return;
    }
    const ctx = this.ctx;
    if (!ctx || !this.out) return;
    const buf = ctx.createBuffer(1, pcm.length, 48000);
    const chan = new Float32Array(pcm.length);
    chan.set(pcm);
    buf.copyToChannel(chan, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.out);
    const now = ctx.currentTime;
    if (this.nextTime < now + 0.005) {
      // Queue ran dry (first frame or a gap): restart the schedule behind the jitter target.
      if (this.nextTime > 0) this.stats.underruns++;
      this.nextTime = now + this.stats.bufferMs / 1000;
    }
    src.start(this.nextTime);
    this.nextTime += buf.duration;
    let peak = 0;
    for (let i = 0; i < pcm.length; i += 4) {
      const v = Math.abs(pcm[i]!);
      if (v > peak) peak = v;
    }
    if (peak > this.peak) this.peak = peak;
    const t = performance.now();
    if (t - this.lastStatsAt > 100) {
      this.lastStatsAt = t;
      const level = this.peak > 0 ? 20 * Math.log10(this.peak) : -60;
      this.peak = 0;
      this.stats = { ...this.stats, level: Math.max(-60, level) };
      this.onStats?.({ ...this.stats, bufferMs: Math.max(0, Math.round((this.nextTime - now) * 1000)) });
    }
  }

  private wcTs = 0;
  private async push(opus: Uint8Array) {
    if (!this.node && !this.out) return;
    if (this.wc) {
      this.wc.decode(new EncodedAudioChunk({ type: 'key', timestamp: this.wcTs, data: opus }));
      this.wcTs += 20000;
      return;
    }
    if (!this.decoder) return;
    const { channelData } = await this.decoder.decodeFrame(opus);
    const pcm = channelData[0];
    if (!pcm) return;
    this.emitPcm(new Float32Array(pcm));
  }

  async detach() {
    for (const u of this.unsubs) u();
    this.unsubs = [];
    this.decoder?.free();
    this.decoder = null;
    try {
      this.wc?.close();
    } catch {
      /* already closed */
    }
    this.wc = null;
    this.node?.disconnect();
    this.node = null;
    this.out?.disconnect();
    this.out = null;
    await this.ctx?.close();
    this.ctx = null;
  }
}

/** AudioWorkletProcessor: ring buffer + adaptive jitter target (80–300 ms) + 25 Hz level report. */
const WORKLET_SRC = `
class GlyphJitter extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buf = new Float32Array(48000 * 2); // 2 s ring
    this.r = 0; this.w = 0; this.fill = 0;
    this.targetMs = 120; this.minMs = 80; this.maxMs = 300;
    this.started = false; this.underruns = 0; this.tick = 0; this.peak = 0;
    this.port.onmessage = (m) => {
      const d = m.data;
      if (d.type === 'target') this.targetMs = Math.min(this.maxMs, Math.max(this.minMs, d.ms));
      if (d.type === 'pcm') this.write(d.pcm);
    };
  }
  write(pcm) {
    for (let i = 0; i < pcm.length; i++) {
      this.buf[this.w] = pcm[i]; this.w = (this.w + 1) % this.buf.length;
      if (this.fill < this.buf.length) this.fill++; else this.r = (this.r + 1) % this.buf.length;
    }
    if (!this.started && this.fill >= this.targetMs * 48) this.started = true;
    // overfull: drop down to target to keep glass-to-ear latency bounded
    const cap = (this.targetMs + 100) * 48;
    if (this.fill > cap) { const drop = this.fill - this.targetMs * 48; this.r = (this.r + drop) % this.buf.length; this.fill -= drop; }
  }
  process(_in, outputs) {
    const out = outputs[0][0];
    if (!this.started) { out.fill(0); }
    else if (this.fill < out.length) {
      out.fill(0); this.underruns++; this.started = false;
      this.targetMs = Math.min(this.maxMs, this.targetMs + 40); // adapt up on underrun
    } else {
      for (let i = 0; i < out.length; i++) { const v = this.buf[this.r]; out[i] = v; this.r = (this.r + 1) % this.buf.length; const a = Math.abs(v); if (a > this.peak) this.peak = a; }
      this.fill -= out.length;
    }
    if (++this.tick % 15 === 0) { // ~25 Hz at 128-frame quanta
      const level = this.peak > 0 ? 20 * Math.log10(this.peak) : -60; this.peak = 0;
      if (this.tick % 1500 === 0 && this.underruns === 0 && this.targetMs > this.minMs) this.targetMs -= 10; // adapt down slowly
      this.port.postMessage({ type: 'stats', bufferMs: Math.round(this.fill / 48), underruns: this.underruns, level: Math.max(-60, level) });
    }
    return true;
  }
}
registerProcessor('glyph-jitter', GlyphJitter);
`;
